// ============================================================
// 玩家实体 - 第一人称控制器
// ============================================================
import * as THREE from 'three';
import {
  PLAYER_COLLISION_STEP,
  PLAYER_HEIGHT,
  PLAYER_RADIUS,
  PLAYER_SPEED,
  PLAYER_SPRINT_MULT,
  MOUSE_SENSITIVITY,
} from '../config';
import type { WallAABB } from '../systems/MazeGenerator';

export class Player {
  /** 摄像机位置（玩家眼睛位置） */
  position = new THREE.Vector3();
  /** 移动速度向量 */
  velocity = new THREE.Vector3();
  /** 欧拉角（yaw绕Y轴, pitch绕X轴） */
  yaw = 0;
  pitch = 0;

  /** 移动意图 */
  readonly moveIntent = new THREE.Vector3();
  /** 是否冲刺 */
  sprinting = false;

  /** 设置玩家在世界中的位置 */
  setPosition(x: number, y: number, z: number): void {
    this.position.set(x, y, z);
  }

  /** 处理鼠标旋转 */
  rotate(mouseDX: number, mouseDY: number): void {
    this.yaw -= mouseDX * MOUSE_SENSITIVITY;
    this.pitch -= mouseDY * MOUSE_SENSITIVITY;
    this.pitch = Math.max(-Math.PI / 2.5, Math.min(Math.PI / 2.5, this.pitch));
  }

  /**
   * 更新玩家位置，直接从相机四元数推导移动方向
   */
  update(delta: number, keys: Set<string>, colliders: WallAABB[], camera: THREE.Camera): void {
    // 从相机四元数提取前/右方向（水平面投影）
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    forward.y = 0;
    forward.normalize();

    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
    right.y = 0;
    right.normalize();

    // 计算移动意图
    this.moveIntent.set(0, 0, 0);
    if (keys.has('KeyW') || keys.has('ArrowUp')) this.moveIntent.add(forward);
    if (keys.has('KeyS') || keys.has('ArrowDown')) this.moveIntent.sub(forward);
    if (keys.has('KeyA') || keys.has('ArrowLeft')) this.moveIntent.sub(right);
    if (keys.has('KeyD') || keys.has('ArrowRight')) this.moveIntent.add(right);

    if (this.moveIntent.lengthSq() > 0) {
      this.moveIntent.normalize();
    }

    this.sprinting = keys.has('ShiftLeft') || keys.has('ShiftRight');
    const speed = PLAYER_SPEED * (this.sprinting ? PLAYER_SPRINT_MULT : 1);

    const displacement = this.moveIntent.clone().multiplyScalar(speed * delta);
    const distance = displacement.length();
    const startX = this.position.x;
    const startZ = this.position.z;

    if (distance > 0) {
      const steps = Math.max(1, Math.ceil(distance / PLAYER_COLLISION_STEP));
      displacement.divideScalar(steps);

      for (let i = 0; i < steps; i++) {
        this.resolveCollision(displacement, colliders);
        this.resolvePenetration(colliders);
      }
    }

    this.velocity.set(this.position.x - startX, 0, this.position.z - startZ);
  }

  /**
   * AABB vs 圆形碰撞检测 + 滑动响应
   */
  private resolveCollision(displacement: THREE.Vector3, colliders: WallAABB[]): void {
    const px = this.position.x;
    const pz = this.position.z;
    const r = PLAYER_RADIUS;

    let newX = px + displacement.x;
    let newZ = pz + displacement.z;

    // X轴碰撞
    for (const wall of colliders) {
      const closestX = Math.max(wall.minX, Math.min(newX, wall.maxX));
      const closestZ = Math.max(wall.minZ, Math.min(pz, wall.maxZ));
      const dx = newX - closestX;
      const dz = pz - closestZ;

      if (dx * dx + dz * dz < r * r) {
        if (displacement.x > 0) {
          newX = wall.minX - r;
        } else if (displacement.x < 0) {
          newX = wall.maxX + r;
        }
      }
    }

    // Z轴碰撞
    for (const wall of colliders) {
      const closestX = Math.max(wall.minX, Math.min(newX, wall.maxX));
      const closestZ = Math.max(wall.minZ, Math.min(newZ, wall.maxZ));
      const dx = newX - closestX;
      const dz = newZ - closestZ;

      if (dx * dx + dz * dz < r * r) {
        if (displacement.z > 0) {
          newZ = wall.minZ - r;
        } else if (displacement.z < 0) {
          newZ = wall.maxZ + r;
        }
      }
    }

    this.position.set(newX, PLAYER_HEIGHT, newZ);
  }

  /**
   * 防止墙角或低帧率下的数值误差让玩家停留在墙体内部。
   */
  private resolvePenetration(colliders: WallAABB[]): void {
    const r = PLAYER_RADIUS;
    const rSq = r * r;
    const epsilon = 0.001;

    for (let iteration = 0; iteration < 3; iteration++) {
      let pushed = false;

      for (const wall of colliders) {
        const closestX = Math.max(wall.minX, Math.min(this.position.x, wall.maxX));
        const closestZ = Math.max(wall.minZ, Math.min(this.position.z, wall.maxZ));
        const dx = this.position.x - closestX;
        const dz = this.position.z - closestZ;
        const distanceSq = dx * dx + dz * dz;

        if (distanceSq >= rSq) continue;

        if (distanceSq > 0.000001) {
          const distance = Math.sqrt(distanceSq);
          const push = r - distance + epsilon;
          this.position.x += (dx / distance) * push;
          this.position.z += (dz / distance) * push;
        } else {
          const pushLeft = Math.abs(this.position.x - wall.minX);
          const pushRight = Math.abs(wall.maxX - this.position.x);
          const pushUp = Math.abs(this.position.z - wall.minZ);
          const pushDown = Math.abs(wall.maxZ - this.position.z);
          const minPush = Math.min(pushLeft, pushRight, pushUp, pushDown);

          if (minPush === pushLeft) this.position.x = wall.minX - r - epsilon;
          else if (minPush === pushRight) this.position.x = wall.maxX + r + epsilon;
          else if (minPush === pushUp) this.position.z = wall.minZ - r - epsilon;
          else this.position.z = wall.maxZ + r + epsilon;
        }

        pushed = true;
      }

      if (!pushed) break;
    }

    this.position.y = PLAYER_HEIGHT;
  }
}
